import { useCallback, useEffect, useRef, useState } from 'react';
import { THRALL_CONSCRIPT_CHEAPER_HAND_DURATION_MS } from '../config/thrallConscriptCheaperHandEffect';

const THRALL_CONSCRIPT_ORDER_ID = 35;

function pulseInstanceIds({
  instanceIds,
  setPulseUntilByInstanceId,
  setPulseTick,
  schedulePulseClear,
}) {
  if (!Array.isArray(instanceIds) || instanceIds.length === 0) {
    return;
  }

  const until = Date.now() + THRALL_CONSCRIPT_CHEAPER_HAND_DURATION_MS;
  setPulseUntilByInstanceId((prev) => {
    const next = { ...prev };
    instanceIds.forEach((instanceId) => {
      const sid = String(instanceId);
      next[sid] = until;
      schedulePulseClear(instanceId, THRALL_CONSCRIPT_CHEAPER_HAND_DURATION_MS);
    });
    return next;
  });
  setPulseTick((tick) => tick + 1);
}

/**
 * Thrall Conscript (35): cheaper.png on unit 35 hand cards for 1s each time
 * enriched hand cost drops (deploy discount applied server-side in enrichOrder).
 */
export function useThrallConscriptCheaperPulse(gameState, viewerUserId) {
  const prevThrallCostByInstanceIdRef = useRef({});
  const gameIdRef = useRef(null);
  const timeoutIdsRef = useRef(new Map());
  const [pulseUntilByInstanceId, setPulseUntilByInstanceId] = useState({});
  const [pulseTick, setPulseTick] = useState(0);

  const clearAllTimeouts = useCallback(() => {
    timeoutIdsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    timeoutIdsRef.current.clear();
  }, []);

  const schedulePulseClear = useCallback((instanceId, durationMs) => {
    const sid = String(instanceId);
    const existingTimeoutId = timeoutIdsRef.current.get(sid);
    if (existingTimeoutId != null) {
      window.clearTimeout(existingTimeoutId);
    }

    const timeoutId = window.setTimeout(() => {
      timeoutIdsRef.current.delete(sid);
      setPulseUntilByInstanceId((prev) => {
        if (!prev[sid]) {
          return prev;
        }
        const next = { ...prev };
        delete next[sid];
        return next;
      });
      setPulseTick((tick) => tick + 1);
    }, durationMs);

    timeoutIdsRef.current.set(sid, timeoutId);
  }, []);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) {
      return undefined;
    }

    const player =
      gameState.state[viewerUserId] ?? gameState.state[String(viewerUserId)];
    if (!player) {
      return undefined;
    }

    const gameId = gameState.gameId != null ? String(gameState.gameId) : '_';

    if (gameIdRef.current !== gameId) {
      gameIdRef.current = gameId;
      prevThrallCostByInstanceIdRef.current = {};
      return undefined;
    }

    const thrallCards = (player.hand || []).filter(
      (order) => order && Number(order.id) === THRALL_CONSCRIPT_ORDER_ID && order.instanceId
    );

    const activeInstanceIds = new Set();
    const instanceIdsToPulse = [];

    thrallCards.forEach((order) => {
      const sid = String(order.instanceId);
      activeInstanceIds.add(sid);

      if (typeof order.cost !== 'number') {
        throw new Error(
          `useThrallConscriptCheaperPulse: Thrall Conscript hand card ${sid} is missing numeric cost`
        );
      }

      const prevCost = prevThrallCostByInstanceIdRef.current[sid];
      if (prevCost !== undefined && order.cost < prevCost) {
        instanceIdsToPulse.push(order.instanceId);
      }
      prevThrallCostByInstanceIdRef.current[sid] = order.cost;
    });

    Object.keys(prevThrallCostByInstanceIdRef.current).forEach((sid) => {
      if (!activeInstanceIds.has(sid)) {
        delete prevThrallCostByInstanceIdRef.current[sid];
      }
    });

    if (instanceIdsToPulse.length > 0) {
      pulseInstanceIds({
        instanceIds: instanceIdsToPulse,
        setPulseUntilByInstanceId,
        setPulseTick,
        schedulePulseClear,
      });
    }
  }, [
    gameState,
    gameState?.gameId,
    gameState?.stateVersion,
    viewerUserId,
    schedulePulseClear,
  ]);

  const isThrallConscriptCheaperOverlayActive = useCallback(
    (instanceId) => {
      void pulseTick;
      if (instanceId == null) {
        return false;
      }
      const until = pulseUntilByInstanceId[String(instanceId)];
      return typeof until === 'number' && until > Date.now();
    },
    [pulseUntilByInstanceId, pulseTick]
  );

  const reset = useCallback(() => {
    clearAllTimeouts();
    prevThrallCostByInstanceIdRef.current = {};
    gameIdRef.current = null;
    setPulseUntilByInstanceId({});
    setPulseTick((tick) => tick + 1);
  }, [clearAllTimeouts]);

  useEffect(() => () => clearAllTimeouts(), [clearAllTimeouts]);

  return {
    isThrallConscriptCheaperOverlayActive,
    reset,
  };
}

export default useThrallConscriptCheaperPulse;
